import React from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import Media from 'react-media';
import isEmpty from 'lodash.isempty';

import trackAction from 'utils/tracking';

import { getPageOnHostByID } from 'lib/api';
import { requireQueryByName } from 'lib/dependencies';

import NewsCard from 'components/NewsCard';
import Breadcrumbs from 'components/Core/Breadcrumbs';
import RichText from 'components/Core/RichText';
import Carousel from 'components/Core/Carousel';
import SocialShareBar from 'components/Core/SocialShareBar';
import Embed from 'components/Core/Embed';

import { NewsPageFigure as NewsPageFigureElem, ReadMoreLink as ReadMoreLinkElem } from './NewsPage.styles';

const mediaQueries = {
  sm: '(max-width: 767px)',
  md: '(min-width: 768px) and (max-width: 1023px)',
  lg: '(min-width: 1024px)',
};

const getOtherNews = (props) => {
  return (
    <div key={props.url} className="h-full px-2 lg:px-0">
      <Media queries={mediaQueries} defaultMatches={{ lg: true }}>
        {(matches) => {
          if (matches.sm || matches.md) {
            return (
              <NewsCard
                standalone
                onClick={() => {
                  trackAction('other-news', props.shortTitle, 'article-click');
                }}
                {...props}
              />
            );
          }
          return (
            <NewsCard
              onClick={() => {
                trackAction('other-news', props.shortTitle, 'article-click');
              }}
              {...props}
            />
          );
        }}
      </Media>
    </div>
  );
};

const NewsPage = ({ page }) => {
  const {
    title,
    bodyRt,
    introduction,
    optionalBody,
    parent,
    image,
    otherNews = [],
    breadcrumbs,
    socialShareTitle,
  } = page;

  return (
    <div className="container mx-auto px-4">
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <div className="u-block-margins">
        <h2 className="lg:h1 mb-4 text-secondary">{parent.title}</h2>
        <div className="flex flex-wrap lg:justify-between -mx-4">
          <div className="flex-full lg:flex-7/12 max-w-full lg:max-w-7/12 p-4">
            <h1 className="h3 lg:h2 mb-6 lg:mb-10">{title}</h1>
            <NewsPageFigureElem className="mb-6 lg:mb-10">
              <Media queries={mediaQueries} defaultMatches={{ lg: true }}>
                {(matches) => {
                  if (matches.sm) {
                    return (
                      <img
                        src={image.sm.url}
                        alt={image.title}
                        className="w-full h-full rounded-lg object-cover object-center"
                      />
                    );
                  }
                  return (
                    <img
                      src={image.lg.url}
                      alt={image.title}
                      className="w-full h-full rounded-lg object-cover object-center"
                    />
                  );
                }}
              </Media>
            </NewsPageFigureElem>
            <p className="font-bold mb-6 lg:mb-10">{introduction}</p>
            <RichText blockMargin={false} content={bodyRt} />
            {(optionalBody || []).map((block) => {
              if (block.blockType === 'RichTextBlock') {
                return <RichText key={`${block.blockType}-${block.id}`} blockMargin={false} content={block.value} />;
              }
              if (block.blockType === 'CustomEmbedBlock') {
                return (
                  <div key={`${block.blockType}-${block.id}`} className="mb-6 lg:mb-10">
                    <Embed title={block.title} {...block.embed} />
                  </div>
                );
              }
              return <div key={`${block.blockType}-${block.id}`} />;
            })}
            <div className="px-2">
              <SocialShareBar className="u-block-margins" socialShareTitle={socialShareTitle} articleTitle={title} />
            </div>
          </div>
          {!isEmpty(otherNews) && (
            <aside className="flex-full lg:flex-1/3 max-w-full lg:max-w-1/3">
              <div className="p-4 lg:bg-activity-sport-400 rounded-base">
                <h2 className="h3 lg:h2 mb-6 lg:mb-10">Overige nieuwsitems</h2>
                <Media queries={mediaQueries} defaultMatches={{ lg: true }}>
                  {(matches) => {
                    if (matches.lg) {
                      return otherNews.map(getOtherNews);
                    }
                    if (matches.md) {
                      return (
                        <Carousel
                          items={otherNews}
                          totalNumber={otherNews.length}
                          visibleSlides={2}
                          slideRenderer={getOtherNews}
                        />
                      );
                    }
                    return <Carousel items={otherNews} totalNumber={otherNews.length} slideRenderer={getOtherNews} />;
                  }}
                </Media>
                <div className="flex justify-end">
                  <Link href="/[...slug]" as={parent.url} passHref>
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <ReadMoreLinkElem
                      onClick={() => {
                        trackAction('other-news', 'view-all-news-items', 'view-all');
                      }}
                      onKeyUp={(event) => {
                        if (event.key === 'Enter') {
                          trackAction('other-news', 'view-all-news-items', 'view-all');
                        }
                      }}
                      className="underline text-primary hover:text-primary-600 focus:text-primary-600"
                    >
                      Bekijk alle nieuwsitems
                    </ReadMoreLinkElem>
                  </Link>
                </div>
              </div>
            </aside>
          )}
        </div>
      </div>
    </div>
  );
};

NewsPage.getCustomInitialProps = async ({ id, contentType, hostname, previewData }) => {
  const query = requireQueryByName('NewsPage');
  const token = previewData ? previewData.token : '';
  const { page } = await getPageOnHostByID({
    id,
    contentType,
    query,
    token,
    hostname,
  });

  return {
    page,
  };
};

NewsPage.propTypes = {
  page: PropTypes.shape({
    title: PropTypes.string.isRequired,
    bodyRt: PropTypes.string.isRequired,
    introduction: PropTypes.string.isRequired,
    optionalBody: PropTypes.arrayOf(PropTypes.shape({})),
    parent: PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string,
    }).isRequired,
    image: PropTypes.shape({
      title: PropTypes.string,
      lg: PropTypes.shape({
        url: PropTypes.string.isRequired,
      }),
      sm: PropTypes.shape({
        url: PropTypes.string.isRequired,
      }),
    }).isRequired,
    otherNews: PropTypes.arrayOf(NewsPage),
    breadcrumbs: PropTypes.arrayOf({
      url: PropTypes.string,
      title: PropTypes.string,
    }).isRequired,
    socialShareTitle: PropTypes.string,
  }).isRequired,
};

export default NewsPage;
